import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Zap, Settings, Play, Pause, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AutomationModule = ({
  t,
  isMobile,
  session,
  savedShellyServer,
  savedShellyAuthKey,
  savedShellyDevices, // [{id: "...", name: "..."}, ...]
  automationLprShellyEnabled,
  setAutomationLprShellyEnabled,
  automationLprRejectedShellyEnabled,
  setAutomationLprRejectedShellyEnabled,
  automationDisarmedCallEnabled,
  setAutomationDisarmedCallEnabled,
  automationDisarmedCallTo,
  setAutomationDisarmedCallTo,
  automationDisarmedCallLanguage,
  setAutomationDisarmedCallLanguage,
  automationDisarmedCallText,
  setAutomationDisarmedCallText,
  automationArmedCallEnabled,
  setAutomationArmedCallEnabled,
  automationArmedCallTo,
  setAutomationArmedCallTo,
  automationArmedCallLanguage,
  setAutomationArmedCallLanguage,
  automationArmedCallText,
  setAutomationArmedCallText,
  savedTwilioAccountSid,
  savedTwilioAuthToken,
  savedTwilioPhoneNumber,
}) => {
  // Per-device state: { [deviceId]: { loading, statusLoading, status } }
  const [deviceStates, setDeviceStates] = useState({});
  const initialLoadDone = useRef(false);

  const isShellyConfigured = savedShellyServer && savedShellyAuthKey;
  const devices = savedShellyDevices || [];

  const updateDeviceState = (deviceId, updates) => {
    setDeviceStates(prev => ({
      ...prev,
      [deviceId]: { ...(prev[deviceId] || {}), ...updates }
    }));
  };

  const fetchDeviceStatus = async (deviceId) => {
    if (!isShellyConfigured || !deviceId || !session?.userToken) return;
    updateDeviceState(deviceId, { statusLoading: true });
    try {
      const response = await axios.get(`${API}/shelly/status/${deviceId}`, {
        headers: { 'X-User-Session-Token': session.userToken }
      });
      if (response.data.success) {
        updateDeviceState(deviceId, {
          status: {
            online: response.data.online,
            is_on: response.data.is_on,
            power: response.data.power
          },
          statusLoading: false
        });
      } else {
        updateDeviceState(deviceId, { statusLoading: false });
      }
    } catch (error) {
      updateDeviceState(deviceId, { statusLoading: false });
    }
  };

  // Initial load
  useEffect(() => {
    if (initialLoadDone.current) return;
    if (!isShellyConfigured || !session?.userToken || devices.length === 0) return;

    const loadAll = async () => {
      await new Promise(r => setTimeout(r, 500));
      for (const dev of devices) {
        if (dev.id) await fetchDeviceStatus(dev.id);
      }
      initialLoadDone.current = true;
    };
    loadAll();
  }, [savedShellyServer, savedShellyAuthKey, session?.userToken, devices.length]);

  const controlDevice = async (deviceId, turn, deviceName) => {
    if (!isShellyConfigured) { alert(t('automation_not_configured')); return; }
    updateDeviceState(deviceId, { loading: true });
    try {
      const response = await axios.post(`${API}/shelly/control`, {
        device_id: deviceId, turn, channel: 0
      }, { headers: { 'X-User-Session-Token': session.userToken } });
      if (response.data.success) {
        setTimeout(() => fetchDeviceStatus(deviceId), 500);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Fehler';
      alert(`${deviceName}: ${errorMsg}`);
    } finally {
      updateDeviceState(deviceId, { loading: false });
    }
  };

  const refreshAllStatuses = () => {
    devices.forEach(dev => { if (dev.id) fetchDeviceStatus(dev.id); });
  };

  const getStatusColor = (status, statusLoading) => {
    if (statusLoading) return '#fbbf24';
    if (!status) return '#9ca3af';
    if (!status.online) return '#6b7280';
    if (status.is_on) return '#10b981';
    return '#ef4444';
  };

  const getStatusText = (status, statusLoading) => {
    if (statusLoading) return t('automation_loading') || 'Laden...';
    if (!status) return t('automation_not_connected');
    if (!status.online) return t('automation_offline') || 'Offline';
    if (status.is_on) return t('automation_on');
    return t('automation_off');
  };

  const anyLoading = devices.some(d => deviceStates[d.id]?.statusLoading);

  const DeviceCard = ({ device, index }) => {
    const ds = deviceStates[device.id] || {};
    const { loading = false, statusLoading = false, status = null } = ds;

    return (
      <Card className="panel lightCard">
        <CardContent>
          <div style={{padding: isMobile ? '12px' : '16px'}}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: isMobile ? '12px' : '16px'
            }}>
              <div style={{minWidth: 0, flex: 1}}>
                <div style={{
                  fontSize: isMobile ? '16px' : '18px', fontWeight: 'bold', color: '#1f2937',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {device.name || `${t('automation_device')} ${index + 1}`}
                </div>
                <div className="small" style={{
                  color: '#6b7280', fontFamily: 'monospace',
                  fontSize: isMobile ? '10px' : '12px', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {device.id}
                </div>
                {status?.power != null && (
                  <div className="small" style={{color: '#059669', fontSize: isMobile ? '11px' : '12px', marginTop: '4px'}}>
                    {status.power.toFixed(1)} W
                  </div>
                )}
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <div style={{
                  width: '12px', height: '12px', borderRadius: '50%',
                  backgroundColor: getStatusColor(status, statusLoading),
                  flexShrink: 0, transition: 'background-color 0.3s',
                  boxShadow: status?.is_on ? '0 0 8px #10b981' : 'none'
                }} title={getStatusText(status, statusLoading)} />
                <Button variant="ghost" size="sm" style={{padding: '4px', minWidth: 'auto'}}
                  onClick={() => fetchDeviceStatus(device.id)} disabled={statusLoading}>
                  <RefreshCw size={14} className={statusLoading ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>

            <div style={{display: 'flex', gap: '8px'}}>
              <Button
                variant={status?.is_on ? 'default' : 'outline'}
                style={{
                  flex: 1, minHeight: isMobile ? '44px' : '40px',
                  backgroundColor: status?.is_on ? '#10b981' : undefined,
                  borderColor: status?.is_on ? '#10b981' : undefined,
                  color: status?.is_on ? 'white' : undefined
                }}
                disabled={!isShellyConfigured || loading || (status && !status.online)}
                onClick={() => controlDevice(device.id, 'on', device.name)}
                data-testid={`device-${index}-on-btn`}
              >
                {loading ? <Loader2 size={16} className="animate-spin" style={{marginRight: '6px'}} />
                  : <Play size={16} style={{marginRight: '6px'}} />}
                {t('automation_on')}
              </Button>
              <Button
                variant={status && !status.is_on ? 'default' : 'outline'}
                style={{
                  flex: 1, minHeight: isMobile ? '44px' : '40px',
                  backgroundColor: status && !status.is_on && status.online ? '#6b7280' : undefined,
                  borderColor: status && !status.is_on && status.online ? '#6b7280' : undefined,
                  color: status && !status.is_on && status.online ? 'white' : undefined
                }}
                disabled={!isShellyConfigured || loading || (status && !status.online)}
                onClick={() => controlDevice(device.id, 'off', device.name)}
                data-testid={`device-${index}-off-btn`}
              >
                {loading ? <Loader2 size={16} className="animate-spin" style={{marginRight: '6px'}} />
                  : <Pause size={16} style={{marginRight: '6px'}} />}
                {t('automation_off')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Disarmed call - persist field changes
  const persistDisarmedCall = async (patch) => {
    try {
      await axios.patch(`${API}/user/settings`, { preferences: patch }, { headers: { 'X-User-Session-Token': session.userToken } });
    } catch (_) {}
  };

  const [testingCall, setTestingCall] = useState(null); // holds rule-key while running

  const makeTestCall = async (ruleKey, to, text, language) => {
    if (!to || !text) {
      alert(t('automation_call_test_missing'));
      return;
    }
    setTestingCall(ruleKey);
    try {
      const res = await axios.post(`${API}/twilio/test-call`, {
        to_number: to, text: text, language: language,
      }, { headers: { 'X-User-Session-Token': session.userToken } });
      alert(t('automation_call_test_ok') + (res.data?.sid ? ` (SID: ${res.data.sid})` : ''));
    } catch (e) {
      alert(t('automation_call_test_fail') + ': ' + (e.response?.data?.detail || e.message));
    } finally {
      setTestingCall(null);
    }
  };

  const twilioConfigured = !!(savedTwilioAccountSid && savedTwilioAuthToken && savedTwilioPhoneNumber);

  // Generic reusable call-rule card renderer
  const renderCallRule = (rule) => (
    <div key={rule.key} style={{
      padding: '14px 16px',
      marginTop: '14px',
      backgroundColor: rule.enabled ? '#eff6ff' : '#ffffff',
      border: `1px solid ${rule.enabled ? '#3b82f6' : '#e2e8f0'}`,
      borderRadius: '10px',
      transition: 'all 0.2s ease',
    }}>
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap'}}>
        <div style={{flex: '1 1 auto', minWidth: '220px'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '8px',
              backgroundColor: rule.enabled ? '#3b82f6' : '#e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s ease', flexShrink: 0
            }}>
              <span style={{fontSize: '16px', color: rule.enabled ? 'white' : '#64748b'}}>📞</span>
            </div>
            <div>
              <div style={{fontSize: isMobile ? '13px' : '14px', fontWeight: 600, color: '#0f172a'}}>
                {rule.title}
              </div>
              <div style={{fontSize: '12px', color: '#64748b', marginTop: '2px', lineHeight: 1.4}}>
                {rule.desc}
              </div>
            </div>
          </div>
        </div>
        <Switch
          checked={!!rule.enabled}
          onCheckedChange={(val) => rule.setEnabled(val)}
          data-testid={`${rule.key}-toggle`}
        />
      </div>

      <div style={{marginTop: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
        <div>
          <label style={{display: 'block', fontSize: '12px', color: '#475569', marginBottom: '4px', fontWeight: 500}}>
            {t('automation_disarmed_call_to_label')}
          </label>
          <input
            type="tel"
            value={rule.to}
            onChange={(e) => rule.setTo(e.target.value)}
            onBlur={() => persistDisarmedCall({ [`${rule.prefsPrefix}_to`]: (rule.to || '').trim() })}
            placeholder="+4915112345678"
            data-testid={`${rule.key}-to-input`}
            style={{width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px'}}
          />
        </div>
        <div>
          <label style={{display: 'block', fontSize: '12px', color: '#475569', marginBottom: '4px', fontWeight: 500}}>
            {t('automation_disarmed_call_lang_label')}
          </label>
          <select
            value={rule.language}
            onChange={(e) => { rule.setLanguage(e.target.value); persistDisarmedCall({ [`${rule.prefsPrefix}_language`]: e.target.value }); }}
            data-testid={`${rule.key}-lang-select`}
            style={{width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: 'white'}}
          >
            <option value="de-DE">Deutsch (de-DE)</option>
            <option value="en-US">English (en-US)</option>
            <option value="en-GB">English (en-GB)</option>
            <option value="fr-FR">Français (fr-FR)</option>
            <option value="es-ES">Español (es-ES)</option>
            <option value="it-IT">Italiano (it-IT)</option>
            <option value="nl-NL">Nederlands (nl-NL)</option>
            <option value="pl-PL">Polski (pl-PL)</option>
          </select>
        </div>
      </div>

      <div style={{marginTop: '10px'}}>
        <label style={{display: 'block', fontSize: '12px', color: '#475569', marginBottom: '4px', fontWeight: 500}}>
          {t('automation_disarmed_call_text_label')}
        </label>
        <textarea
          value={rule.text}
          onChange={(e) => rule.setText(e.target.value)}
          onBlur={() => persistDisarmedCall({ [`${rule.prefsPrefix}_text`]: rule.text })}
          placeholder={rule.textPlaceholder}
          rows={2}
          data-testid={`${rule.key}-text-input`}
          style={{width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', resize: 'vertical', fontFamily: 'inherit'}}
        />
      </div>

      <div style={{marginTop: '10px', display: 'flex', gap: '8px'}}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => makeTestCall(rule.key, rule.to, rule.text, rule.language)}
          disabled={testingCall === rule.key || !rule.to || !rule.text}
          data-testid={`${rule.key}-test-btn`}
        >
          {testingCall === rule.key ? <Loader2 size={14} className="animate-spin" style={{marginRight: 6}} /> : '📞 '}
          {t('automation_call_test_btn')}
        </Button>
      </div>

      {!twilioConfigured && (
        <div style={{fontSize: '11px', color: '#b45309', marginTop: '8px', padding: '6px 10px', backgroundColor: '#fef3c7', borderRadius: '6px'}}>
          ⚠️ {t('automation_twilio_missing')}
        </div>
      )}
    </div>
  );

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '16px'}}>

      {/* Header */}
      <Card className="panel lightCard" style={{marginTop: 8}}>
        <CardContent>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '12px'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px'}}>
              <Zap size={isMobile ? 24 : 28} color="#f59e0b" />
              <div>
                <div className="panel-title darktxt" style={{fontSize: isMobile ? '16px' : '18px'}}>
                  {t('automation_title')}
                </div>
                <div className="small" style={{fontSize: isMobile ? '12px' : '14px'}}>
                  {t('automation_subtitle')} ({devices.length} {devices.length === 1 ? t('automation_device_single') : t('automation_device_plural')})
                </div>
              </div>
            </div>
            {isShellyConfigured && devices.length > 0 && (
              <Button variant="outline" size="sm" onClick={refreshAllStatuses}
                disabled={anyLoading} style={{minHeight: isMobile ? '36px' : '32px'}}>
                <RefreshCw size={14} className={anyLoading ? 'animate-spin' : ''} style={{marginRight: '6px'}} />
                {t('automation_refresh') || 'Aktualisieren'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Not configured */}
      {!isShellyConfigured && (
        <Card className="panel" style={{backgroundColor: '#fef3c7', border: '1px solid #f59e0b'}}>
          <CardContent>
            <div style={{
              display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '10px' : '12px', flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Settings size={isMobile ? 20 : 24} color="#92400e" style={{flexShrink: 0}} />
              <div>
                <div style={{fontWeight: 'bold', color: '#92400e', fontSize: isMobile ? '14px' : '16px'}}>
                  {t('automation_not_configured')}
                </div>
                <div style={{fontSize: isMobile ? '12px' : '14px', color: '#92400e'}}>
                  {t('automation_not_configured_hint')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Device Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: isMobile ? '12px' : '16px'
      }}>
        {devices.map((device, idx) => (
          <DeviceCard key={device.id || idx} device={device} index={idx} />
        ))}

        {devices.length === 0 && (
          <Card className="panel lightCard" style={{gridColumn: '1 / -1'}}>
            <CardContent>
              <div style={{textAlign: 'center', padding: isMobile ? '30px 16px' : '40px 20px', color: '#6b7280'}}>
                <Zap size={isMobile ? 40 : 48} color="#d1d5db" style={{marginBottom: isMobile ? '12px' : '16px'}} />
                <div style={{fontSize: isMobile ? '14px' : '16px', fontWeight: '500', marginBottom: '8px'}}>
                  {t('automation_no_devices')}
                </div>
                <div className="small" style={{fontSize: isMobile ? '12px' : '14px'}}>
                  {t('automation_no_devices_hint')}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Future Automations */}
      <Card className="panel lightCard">
        <CardContent>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: isMobile ? '12px' : '16px', flexWrap: 'wrap', gap: '8px'
          }}>
            <div className="panel-title darktxt" style={{fontSize: isMobile ? '16px' : '18px'}}>
              {t('automation_rules_title')}
            </div>
          </div>

          {/* Rule 1: LPR Accepted → Shelly Switch1 ON for 10s */}
          {[
            {
              key: 'rule_lpr_accepted',
              enabled: automationLprShellyEnabled,
              onChange: setAutomationLprShellyEnabled,
              title: t('automation_lpr_shelly_title'),
              desc: t('automation_lpr_shelly_desc'),
              testId: 'automation-lpr-shelly-toggle',
              requiresIndex: 0,
            },
            {
              key: 'rule_lpr_rejected',
              enabled: automationLprRejectedShellyEnabled,
              onChange: setAutomationLprRejectedShellyEnabled,
              title: t('automation_lpr_rejected_shelly_title'),
              desc: t('automation_lpr_rejected_shelly_desc'),
              testId: 'automation-lpr-rejected-shelly-toggle',
              requiresIndex: 1,
            },
          ].map((rule, idx) => {
            const missingDevice = devices.length <= rule.requiresIndex;
            return (
              <div key={rule.key} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '16px',
                padding: '14px 16px',
                marginTop: idx > 0 ? '10px' : 0,
                backgroundColor: rule.enabled ? '#ecfdf5' : '#ffffff',
                border: `1px solid ${rule.enabled ? '#10b981' : '#e2e8f0'}`,
                borderRadius: '10px',
                transition: 'all 0.2s ease',
                flexWrap: 'wrap'
              }}>
                <div style={{flex: '1 1 auto', minWidth: '220px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      backgroundColor: rule.enabled ? '#10b981' : '#e2e8f0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      flexShrink: 0
                    }}>
                      <Zap size={18} color={rule.enabled ? '#ffffff' : '#64748b'} />
                    </div>
                    <div>
                      <div style={{fontSize: isMobile ? '13px' : '14px', fontWeight: 600, color: '#0f172a'}}>
                        {rule.title}
                      </div>
                      <div style={{fontSize: '12px', color: '#64748b', marginTop: '2px', lineHeight: 1.4}}>
                        {rule.desc}
                      </div>
                      {rule.enabled && missingDevice && (
                        <div style={{fontSize: '11px', color: '#b45309', marginTop: '4px'}}>
                          ⚠️ {t('automation_missing_device').replace('{n}', rule.requiresIndex + 1)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={!!rule.enabled}
                  onCheckedChange={(val) => rule.onChange(val)}
                  data-testid={rule.testId}
                />
              </div>
            );
          })}

          {!isShellyConfigured && (
            <div style={{fontSize: '12px', color: '#b45309', marginTop: '10px', padding: '8px 12px', backgroundColor: '#fef3c7', borderRadius: '6px'}}>
              ⚠️ {t('automation_lpr_shelly_warning')}
            </div>
          )}

          {/* Rule: Armed → Twilio Voice Call (first position) */}
          {renderCallRule({
            key: 'automation-armed-call',
            prefsPrefix: 'automation_armed_call',
            enabled: automationArmedCallEnabled,
            setEnabled: setAutomationArmedCallEnabled,
            to: automationArmedCallTo,
            setTo: setAutomationArmedCallTo,
            language: automationArmedCallLanguage,
            setLanguage: setAutomationArmedCallLanguage,
            text: automationArmedCallText,
            setText: setAutomationArmedCallText,
            title: t('automation_armed_call_title'),
            desc: t('automation_armed_call_desc'),
            textPlaceholder: t('automation_armed_call_text_placeholder'),
          })}

          {/* Rule: Disarmed → Twilio Voice Call */}
          {renderCallRule({
            key: 'automation-disarmed-call',
            prefsPrefix: 'automation_disarmed_call',
            enabled: automationDisarmedCallEnabled,
            setEnabled: setAutomationDisarmedCallEnabled,
            to: automationDisarmedCallTo,
            setTo: setAutomationDisarmedCallTo,
            language: automationDisarmedCallLanguage,
            setLanguage: setAutomationDisarmedCallLanguage,
            text: automationDisarmedCallText,
            setText: setAutomationDisarmedCallText,
            title: t('automation_disarmed_call_title'),
            desc: t('automation_disarmed_call_desc'),
            textPlaceholder: t('automation_disarmed_call_text_placeholder'),
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomationModule;
